import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import store from '../index';
import { AdminUserWrite, UserLogin } from '../../common/types';
import AdminService from '../../common/api/admin.service';
import Admin from '../../common/model/Admin';
import JwtService, { ID_ACCESS_TOKEN_KEY } from '../../common/jwt.service';
import router from '../../router';
import { BRAND_LOGIN_PAGE } from '../../common/routes.config';

const jwtService = new JwtService();

@Module({
  dynamic: true,
  store,
  name: 'admin',
})
class AdminModule extends VuexModule {
  admin: Admin | null = null;

  token: string | null = jwtService.getToken(ID_ACCESS_TOKEN_KEY);

  service = new AdminService();

  // Set brand when budtender visits brand's site, or when admin enters portal.
  @Mutation
  SET_ADMIN(admin) {
    this.admin = admin;
  }

  @Mutation
  PURGE_AUTH() {
    this.admin = null;
    this.token = null;
  }

  @Mutation
  SET_JWT(token) {
    // Save to Authorization header
    this.service.setHeader(token);
    // Store in localstorage
    jwtService.saveToken(ID_ACCESS_TOKEN_KEY, token);
    this.token = token;
  }

  @Action
  async createAdmin(admin: AdminUserWrite): Promise<void> {
    const response = await this.service.createAdmin(admin);
    if (!response) { return; }
    this.SET_JWT(response.data.data.token);
    this.SET_ADMIN(response.data.data.user);
  }

  @Action
  async setAdminBrand(payload): Promise<void> {
    const response = await this.service.updateAdmin(payload);
    if (!response) { return; }
    this.SET_ADMIN(response.data.data.admin);
  }

  @Action
  async adminLogin(payload: UserLogin): Promise<void> {
    const response = await this.service.loginAdmin(payload);
    if (!response) { return; }
    this.SET_JWT(response.data.data.token);
    this.SET_ADMIN(response.data.data.user);
  }

  @Action
  async fetchAdmin(): Promise<void> {
    if (!this.token) { return; }
    this.service.setHeader(this.token);
    const response = await this.service.fetchAdmin();
    if (!response) {
      this.SET_ADMIN(null);
      this.SET_JWT(null);
      jwtService.destroyToken(ID_ACCESS_TOKEN_KEY);
      router.push(BRAND_LOGIN_PAGE);
      return;
    }
    this.SET_ADMIN(response.data.data.admin);
  }

  @Action
  async logout(): Promise<void> {
    await jwtService.destroyToken(ID_ACCESS_TOKEN_KEY);
    this.PURGE_AUTH();
    router.push(BRAND_LOGIN_PAGE);
  }

  @Action
  async inviteAdmin(payload): Promise<void> {
    await this.service.inviteAdmin(payload);
  }

  @Action
  async activateAdmin(payload): Promise<void> {
    const response = await this.service.activateUser(payload);
    if (!response) { return; }
    this.SET_JWT(response.data.data.token);
    this.SET_ADMIN(response.data.data.user);
  }

  @Action
  async initiateAdminPasswordReset(payload): Promise<void> {
    await this.service.initiatePasswordReset(payload);
  }

  @Action
  async confirmAdminPasswordReset(payload): Promise<void> {
    await this.service.confirmPasswordReset(payload);
  }
}

export default getModule(AdminModule);
